import type {
  ResultsPageConfig,
  ResultsPageComparisonAttribute,
  ResultsPageExitStrategyExtraCtaConfig,
  ResultsPageExitStrategyConfig,
  ResultsPageExitStrategyThankYouConfig,
  ResultsPageExitStrategyStudentLoansInlineConfig,
  ResultsPageLoadingStepConfig,
  ResultsPageModalConfig,
} from '@de/types/types';
import {
  LOADING_STEP_ICON_KEYS,
  MAX_LOADING_STEPS,
  type LoadingStepIconKey,
} from '@de/components/Loading/defaults';

type WarnFn = (message: string) => void;

const ROOT_PATH = 'resultsPage';
const MODAL_FIELDS = new Set([
  'enabled',
  'title',
  'subtitle',
  'disclosureTitle',
  'disclosureContent',
]);
const LOADING_FIELDS = new Set(['enabled', 'title', 'statusLabel', 'steps']);
const LOADING_STEP_FIELDS = new Set(['label', 'icon']);
const LOADING_STEP_ICONS: ReadonlySet<string> = new Set(LOADING_STEP_ICON_KEYS);
export { MAX_LOADING_STEPS };
export const MAX_COMPARE_ATTRIBUTES = 10;
const COMPARISON_FIELDS = new Set([
  'enabled',
  'enableCompare',
  'minSelection',
  'maxSelection',
  'variant',
  'bannerText',
  'ctaText',
  'heading',
  'logo',
  'title',
  'subtitle',
  'buttonText',
  'placeholderText',
  'compareAttributes',
]);
const COMPARISON_STRING_FIELDS = new Set([
  'bannerText',
  'ctaText',
  'heading',
  'logo',
  'title',
  'subtitle',
  'buttonText',
  'placeholderText',
]);
const SUBMISSIONS_FIELDS = new Set(['enabled', 'title', 'subtitle']);
const EXIT_STRATEGY_FIELDS = new Set([
  'thankYou',
  'studentLoansInline',
  'extraCta',
]);
const THANK_YOU_FIELDS = new Set(['enabled']);
const EXTRA_CTA_FIELDS = new Set(['enabled', 'label', 'eventName']);
const STUDENT_LOANS_INLINE_FIELDS = new Set([
  'enabled',
  'title',
  'subtitle',
  'disclosure',
  'partner',
  'cta',
]);

/**
 * Pull `data.root.props.resultsPage` out of a Puck-shaped DSL payload.
 *
 * Missing or non-object config returns `undefined`; malformed fields inside a
 * present config are dropped with warnings. This mirrors `extractDataSources`:
 * runtime rendering should keep going even if an author publishes a partially
 * bad block.
 */
export function extractResultsPageConfig(
  data: unknown,
): ResultsPageConfig | undefined {
  const props = getRootProps(data);
  if (!props || !('resultsPage' in props)) {
    return undefined;
  }

  const raw = props.resultsPage;
  if (!isPlainObject(raw)) {
    warn(`${ROOT_PATH} is not an object; skipping.`);
    return undefined;
  }

  return sanitizeResultsPageConfig(raw, warn);
}

function sanitizeResultsPageConfig(
  raw: Record<string, unknown>,
  warnFn: WarnFn,
): ResultsPageConfig {
  const out: ResultsPageConfig = {};

  for (const key of Object.keys(raw)) {
    if (
      key !== 'flowType' &&
      key !== 'recommender' &&
      key !== 'spotlight' &&
      key !== 'comparison' &&
      key !== 'submissions' &&
      key !== 'exitStrategy' &&
      key !== 'loading'
    ) {
      warnFn(`${ROOT_PATH}.${key} is not supported; skipping.`);
    }
  }

  if (raw.flowType !== undefined) {
    if (typeof raw.flowType === 'string' && raw.flowType.trim()) {
      out.flowType = raw.flowType;
    } else {
      warnFn(`${ROOT_PATH}.flowType is not a non-empty string; skipping.`);
    }
  }

  const recommender = sanitizeModalGroup(
    raw.recommender,
    `${ROOT_PATH}.recommender`,
    warnFn,
  );
  if (recommender) {
    out.recommender = recommender;
  }

  const spotlight = sanitizeModalGroup(
    raw.spotlight,
    `${ROOT_PATH}.spotlight`,
    warnFn,
  );
  if (spotlight) {
    out.spotlight = spotlight;
  }

  const comparison = sanitizeComparison(
    raw.comparison,
    `${ROOT_PATH}.comparison`,
    warnFn,
  );
  if (comparison) {
    out.comparison = comparison;
  }

  const submissions = sanitizeSubmissions(
    raw.submissions,
    `${ROOT_PATH}.submissions`,
    warnFn,
  );
  if (submissions) {
    out.submissions = submissions;
  }

  const exitStrategy = sanitizeExitStrategy(
    raw.exitStrategy,
    `${ROOT_PATH}.exitStrategy`,
    warnFn,
  );
  if (exitStrategy) {
    out.exitStrategy = exitStrategy;
  }

  const loading = sanitizeLoading(raw.loading, `${ROOT_PATH}.loading`, warnFn);
  if (loading) {
    out.loading = loading;
  }

  return out;
}

function sanitizeLoading(
  raw: unknown,
  path: string,
  warnFn: WarnFn,
): ResultsPageConfig['loading'] | undefined {
  if (raw === undefined) {
    return undefined;
  }
  if (!isPlainObject(raw)) {
    warnFn(`${path} is not an object; skipping.`);
    return undefined;
  }

  const loading: NonNullable<ResultsPageConfig['loading']> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!LOADING_FIELDS.has(key)) {
      warnFn(`${path}.${key} is not supported; skipping.`);
      continue;
    }
    if (key === 'enabled') {
      if (typeof value === 'boolean') {
        loading.enabled = value;
      } else {
        warnFn(`${path}.enabled is not a boolean; skipping.`);
      }
      continue;
    }
    if (key === 'title' || key === 'statusLabel') {
      if (typeof value === 'string') {
        loading[key] = value;
      } else {
        warnFn(`${path}.${key} is not a string; skipping.`);
      }
      continue;
    }
    if (key === 'steps') {
      const steps = sanitizeLoadingSteps(value, `${path}.steps`, warnFn);
      if (steps.length > 0) {
        loading.steps = steps;
      }
    }
  }
  return Object.keys(loading).length > 0 ? loading : undefined;
}

function sanitizeLoadingSteps(
  raw: unknown,
  path: string,
  warnFn: WarnFn,
): ResultsPageLoadingStepConfig[] {
  if (raw === undefined) {
    return [];
  }
  if (!Array.isArray(raw)) {
    warnFn(`${path} is not an array; skipping.`);
    return [];
  }

  if (raw.length > MAX_LOADING_STEPS) {
    warnFn(
      `${path} exceeds ${MAX_LOADING_STEPS} steps; using the first ${MAX_LOADING_STEPS}.`,
    );
  }

  return raw
    .slice(0, MAX_LOADING_STEPS)
    .reduce<ResultsPageLoadingStepConfig[]>((acc, item, index) => {
      if (!isPlainObject(item)) {
        warnFn(`${path}[${index}] is not an object; skipping.`);
        return acc;
      }
      for (const key of Object.keys(item)) {
        if (!LOADING_STEP_FIELDS.has(key)) {
          warnFn(`${path}[${index}].${key} is not supported; skipping.`);
        }
      }
      const { label } = item;
      if (typeof label !== 'string' || label.trim() === '') {
        warnFn(`${path}[${index}].label is not a non-empty string; skipping.`);
        return acc;
      }
      const step: ResultsPageLoadingStepConfig = { label };
      if (item.icon !== undefined) {
        if (
          typeof item.icon === 'string' &&
          LOADING_STEP_ICONS.has(item.icon)
        ) {
          step.icon = item.icon as LoadingStepIconKey;
        } else {
          warnFn(
            `${path}[${index}].icon is not a supported loading icon; skipping.`,
          );
        }
      }
      acc.push(step);
      return acc;
    }, []);
}

function sanitizeModalGroup(
  raw: unknown,
  path: string,
  warnFn: WarnFn,
): ResultsPageConfig['recommender'] | undefined {
  if (raw === undefined) {
    return undefined;
  }
  if (!isPlainObject(raw)) {
    warnFn(`${path} is not an object; skipping.`);
    return undefined;
  }

  for (const key of Object.keys(raw)) {
    if (key !== 'modal') {
      warnFn(`${path}.${key} is not supported; skipping.`);
    }
  }

  const modal = sanitizeModal(raw.modal, `${path}.modal`, warnFn);
  return modal ? { modal } : {};
}

function sanitizeModal(
  raw: unknown,
  path: string,
  warnFn: WarnFn,
): ResultsPageModalConfig | undefined {
  if (raw === undefined) {
    return undefined;
  }
  if (!isPlainObject(raw)) {
    warnFn(`${path} is not an object; skipping.`);
    return undefined;
  }

  const modal: ResultsPageModalConfig = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!MODAL_FIELDS.has(key)) {
      warnFn(`${path}.${key} is not supported; skipping.`);
      continue;
    }
    if (key === 'enabled') {
      if (typeof value === 'boolean') {
        modal.enabled = value;
      } else {
        warnFn(`${path}.enabled is not a boolean; skipping.`);
      }
      continue;
    }
    if (
      (key === 'title' ||
        key === 'subtitle' ||
        key === 'disclosureTitle' ||
        key === 'disclosureContent') &&
      typeof value === 'string'
    ) {
      modal[key] = value;
    } else {
      warnFn(`${path}.${key} is not a string; skipping.`);
    }
  }
  return modal;
}

function sanitizeComparison(
  raw: unknown,
  path: string,
  warnFn: WarnFn,
): ResultsPageConfig['comparison'] | undefined {
  if (raw === undefined) {
    return undefined;
  }
  if (!isPlainObject(raw)) {
    warnFn(`${path} is not an object; skipping.`);
    return undefined;
  }

  const comparison: NonNullable<ResultsPageConfig['comparison']> = {};
  applyComparisonEnabled(raw, comparison, path, warnFn);

  for (const [key, value] of Object.entries(raw)) {
    if (!COMPARISON_FIELDS.has(key)) {
      warnFn(`${path}.${key} is not supported; skipping.`);
      continue;
    }
    if (key === 'enabled' || key === 'enableCompare') {
      continue;
    }
    if (key === 'variant') {
      if (value === 'POPUP') {
        comparison.variant = value;
      } else {
        warnFn(`${path}.variant must be "POPUP"; skipping.`);
      }
      continue;
    }
    if (key === 'minSelection') {
      if (isPositiveInteger(value)) {
        comparison.minSelection = value;
      } else {
        warnFn(`${path}.${key} is not a positive integer; skipping.`);
      }
      continue;
    }
    if (key === 'maxSelection') {
      if (isPositiveInteger(value)) {
        comparison.maxSelection = value;
      } else {
        warnFn(`${path}.${key} is not a positive integer; skipping.`);
      }
      continue;
    }
    if (COMPARISON_STRING_FIELDS.has(key)) {
      if (typeof value === 'string') {
        comparison[
          key as keyof Pick<
            NonNullable<ResultsPageConfig['comparison']>,
            | 'bannerText'
            | 'ctaText'
            | 'heading'
            | 'logo'
            | 'title'
            | 'subtitle'
            | 'buttonText'
            | 'placeholderText'
          >
        ] = value;
      } else {
        warnFn(`${path}.${key} is not a string; skipping.`);
      }
      continue;
    }
    if (key === 'compareAttributes') {
      const attributes = sanitizeCompareAttributes(
        value,
        `${path}.compareAttributes`,
        warnFn,
      );
      if (attributes.length > 0) {
        comparison.compareAttributes = attributes;
      }
      continue;
    }
  }

  if (
    comparison.minSelection !== undefined &&
    comparison.maxSelection !== undefined &&
    comparison.minSelection > comparison.maxSelection
  ) {
    warnFn(
      `${path}.minSelection is greater than maxSelection; skipping minSelection.`,
    );
    delete comparison.minSelection;
  }

  return comparison;
}

function sanitizeCompareAttributes(
  raw: unknown,
  path: string,
  warnFn: WarnFn,
): ResultsPageComparisonAttribute[] {
  if (raw === undefined) {
    return [];
  }
  if (!Array.isArray(raw)) {
    warnFn(`${path} is not an array; skipping.`);
    return [];
  }
  if (raw.length > MAX_COMPARE_ATTRIBUTES) {
    warnFn(
      `${path} exceeds ${MAX_COMPARE_ATTRIBUTES} rows; using the first ${MAX_COMPARE_ATTRIBUTES}.`,
    );
  }
  return raw
    .slice(0, MAX_COMPARE_ATTRIBUTES)
    .reduce<ResultsPageComparisonAttribute[]>((acc, item, index) => {
      if (!isPlainObject(item)) {
        warnFn(`${path}[${index}] is not an object; skipping.`);
        return acc;
      }
      const { label, key } = item;
      if (typeof label !== 'string' || label.trim() === '') {
        warnFn(`${path}[${index}].label is not a non-empty string; skipping.`);
        return acc;
      }
      if (typeof key !== 'string' || key.trim() === '') {
        warnFn(`${path}[${index}].key is not a non-empty string; skipping.`);
        return acc;
      }
      acc.push({ label, key });
      return acc;
    }, []);
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

function applyComparisonEnabled(
  raw: Record<string, unknown>,
  comparison: NonNullable<ResultsPageConfig['comparison']>,
  path: string,
  warnFn: WarnFn,
): void {
  const hasEnabled = Object.prototype.hasOwnProperty.call(raw, 'enabled');
  const hasEnableCompare = Object.prototype.hasOwnProperty.call(
    raw,
    'enableCompare',
  );
  const enabled = raw.enabled;
  const enableCompare = raw.enableCompare;

  if (hasEnabled && typeof enabled !== 'boolean') {
    warnFn(`${path}.enabled is not a boolean; skipping.`);
  }
  if (hasEnableCompare && typeof enableCompare !== 'boolean') {
    warnFn(`${path}.enableCompare is not a boolean; skipping.`);
  }
  if (
    typeof enabled === 'boolean' &&
    typeof enableCompare === 'boolean' &&
    enabled !== enableCompare
  ) {
    warnFn(
      `${path}.enableCompare conflicts with ${path}.enabled; using ${path}.enabled.`,
    );
  }

  if (typeof enabled === 'boolean') {
    comparison.enabled = enabled;
    return;
  }
  if (typeof enableCompare === 'boolean') {
    comparison.enabled = enableCompare;
  }
}

function sanitizeSubmissions(
  raw: unknown,
  path: string,
  warnFn: WarnFn,
): ResultsPageConfig['submissions'] | undefined {
  if (raw === undefined) {
    return undefined;
  }
  if (!isPlainObject(raw)) {
    warnFn(`${path} is not an object; skipping.`);
    return undefined;
  }

  const submissions: NonNullable<ResultsPageConfig['submissions']> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!SUBMISSIONS_FIELDS.has(key)) {
      warnFn(`${path}.${key} is not supported; skipping.`);
      continue;
    }
    if (key === 'enabled') {
      if (typeof value === 'boolean') {
        submissions.enabled = value;
      } else {
        warnFn(`${path}.enabled is not a boolean; skipping.`);
      }
      continue;
    }
    if ((key === 'title' || key === 'subtitle') && typeof value === 'string') {
      submissions[key] = value;
    } else {
      warnFn(`${path}.${key} is not a string; skipping.`);
    }
  }
  return submissions;
}

function sanitizeExitStrategy(
  raw: unknown,
  path: string,
  warnFn: WarnFn,
): ResultsPageExitStrategyConfig | undefined {
  if (raw === undefined) {
    return undefined;
  }
  if (!isPlainObject(raw)) {
    warnFn(`${path} is not an object; skipping.`);
    return undefined;
  }

  for (const key of Object.keys(raw)) {
    if (!EXIT_STRATEGY_FIELDS.has(key)) {
      warnFn(`${path}.${key} is not supported; skipping.`);
    }
  }

  const thankYou = sanitizeThankYou(raw.thankYou, `${path}.thankYou`, warnFn);
  const studentLoansInline = sanitizeStudentLoansInline(
    raw.studentLoansInline,
    `${path}.studentLoansInline`,
    warnFn,
  );
  const extraCta = sanitizeExtraCta(raw.extraCta, `${path}.extraCta`, warnFn);

  return {
    ...(thankYou ? { thankYou } : {}),
    ...(studentLoansInline ? { studentLoansInline } : {}),
    ...(extraCta ? { extraCta } : {}),
  };
}

function sanitizeThankYou(
  raw: unknown,
  path: string,
  warnFn: WarnFn,
): ResultsPageExitStrategyThankYouConfig | undefined {
  if (raw === undefined) return undefined;
  if (!isPlainObject(raw)) {
    warnFn(`${path} is not an object; skipping.`);
    return undefined;
  }

  const out: ResultsPageExitStrategyThankYouConfig = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!THANK_YOU_FIELDS.has(key)) {
      warnFn(`${path}.${key} is not supported; skipping.`);
      continue;
    }
    if (typeof value === 'boolean') {
      out.enabled = value;
    } else {
      warnFn(`${path}.enabled is not a boolean; skipping.`);
    }
  }
  return Object.keys(out).length ? out : undefined;
}

function sanitizeExtraCta(
  raw: unknown,
  path: string,
  warnFn: WarnFn,
): ResultsPageExitStrategyExtraCtaConfig | undefined {
  if (raw === undefined) return undefined;
  if (!isPlainObject(raw)) {
    warnFn(`${path} is not an object; skipping.`);
    return undefined;
  }

  const out: ResultsPageExitStrategyExtraCtaConfig = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!EXTRA_CTA_FIELDS.has(key)) {
      warnFn(`${path}.${key} is not supported; skipping.`);
      continue;
    }
    if (key === 'enabled') {
      if (typeof value === 'boolean') {
        out.enabled = value;
      } else {
        warnFn(`${path}.enabled is not a boolean; skipping.`);
      }
      continue;
    }
    if (typeof value === 'string') {
      out[key as 'label' | 'eventName'] = value;
    } else {
      warnFn(`${path}.${key} is not a string; skipping.`);
    }
  }
  return out;
}

function sanitizeStudentLoansInline(
  raw: unknown,
  path: string,
  warnFn: WarnFn,
): ResultsPageExitStrategyStudentLoansInlineConfig | undefined {
  if (raw === undefined) {
    return undefined;
  }
  if (!isPlainObject(raw)) {
    warnFn(`${path} is not an object; skipping.`);
    return undefined;
  }

  const out: ResultsPageExitStrategyStudentLoansInlineConfig = {};

  for (const [key, value] of Object.entries(raw)) {
    if (!STUDENT_LOANS_INLINE_FIELDS.has(key)) {
      warnFn(`${path}.${key} is not supported; skipping.`);
      continue;
    }

    if (key === 'enabled') {
      if (typeof value === 'boolean') {
        out.enabled = value;
      } else {
        warnFn(`${path}.enabled is not a boolean; skipping.`);
      }
      continue;
    }

    (out as Record<string, unknown>)[key] = value;
  }

  return out;
}

function getRootProps(data: unknown): Record<string, unknown> | undefined {
  if (!isPlainObject(data)) {
    return undefined;
  }
  const root = data.root;
  if (!isPlainObject(root)) {
    return undefined;
  }
  const props = root.props;
  if (!isPlainObject(props)) {
    return undefined;
  }
  return props;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function warn(message: string): void {
  // eslint-disable-next-line no-console
  console.warn(`[ConfigurableStep] ${message}`);
}
